import { configurationUtils, HttpMethods } from '@codametrix/ui-common';
import { api } from './net';

const { getConfigValueForServiceLine } = configurationUtils;

export const VERTICAL_VIEW_SELECTOR_KEYNAME = 'vertical-view-selector';

/**
 * `resolved` carries an explicit answer: the configuration says the feature is on or off
 * for this tenant. `unavailable` means the read failed and we know nothing — callers must
 * not treat that as "off".
 */
export type VerticalViewSelectorResult =
  | { status: 'resolved'; enabled: boolean }
  | { status: 'unavailable' };

/**
 * The configuration is semantically boolean but stored as the string "true"/"false"
 * (jsonValue is null on these rows), so accept both forms.
 */
const parseBoolean = (raw: unknown): boolean | null => {
  if (typeof raw === 'boolean') {
    return raw;
  }
  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }
  return null;
};

/**
 * Reads `vertical-view-selector` for a tenant and reduces it to a boolean.
 *
 * Resolution is the shared getConfigValueForServiceLine ladder — the same one
 * service-desk's worklist widget resolves this key through — so the nav label and that
 * widget's vertical navigation column cannot disagree about whether the feature is on.
 *
 * The ladder falls back to wildcard-tenant values, which is what makes this work rather
 * than a per-tenant carve-out: the wildcard row is `false`, so a tenant is renamed unless
 * something scoped to it (or its health system) explicitly opts it out with `true`.
 *
 * Reuses the existing configuration-detail endpoint that the configuration management
 * screens read through `fetchConfigDetail`, called via `api()` rather than by dispatching
 * that thunk, because the thunk writes its response into
 * `configurations.configurationDetail`, which backs the admin config drawer.
 */
export async function fetchVerticalViewSelector(
  tenantId: string,
  serviceLineName: string
): Promise<VerticalViewSelectorResult> {
  if (!tenantId || !serviceLineName) {
    return { status: 'unavailable' };
  }

  try {
    const response = await api<CMxAPI.Configuration[]>({
      endpoint: `/configuration/detail/${VERTICAL_VIEW_SELECTOR_KEYNAME}/${tenantId}/v2?includeEmptyConfigurations=true`,
      init: {
        method: HttpMethods.GET
      }
    });

    const values = Array.isArray(response) ? response[0]?.values : undefined;
    if (!Array.isArray(values)) {
      // The server answered and the key has no values at all: not enabled.
      return { status: 'resolved', enabled: false };
    }

    const resolved = getConfigValueForServiceLine(
      values,
      serviceLineName,
      tenantId
    );

    // getConfigValueForServiceLine hands back an array when several values tie on one
    // rung; parseBoolean rejects that, and a tie is treated as no answer. Anything other
    // than an explicit `true` leaves the tenant renamed.
    return {
      status: 'resolved',
      enabled: parseBoolean(resolved) === true
    };
  } catch (error) {
    console.error(
      `[fetchVerticalViewSelector] Failed to read ${VERTICAL_VIEW_SELECTOR_KEYNAME} for tenantId=${tenantId}, serviceLine=${serviceLineName}:`,
      error
    );
    return { status: 'unavailable' };
  }
}
